import { api } from './axios';

/**
 * Duel Spectator Comments API — Комментарии зрителей в дуэлях
 * @see api_duel_spectator_comments_reference.md
 */

/**
 * Создать комментарий зрителя в дуэли
 * @param {string} duelId - ID дуэли
 * @param {Object} data - Данные комментария
 * @param {string} data.content - Текст комментария (1-2000 символов)
 * @param {string} [data.parentCommentId] - ID родительского комментария (для ответов)
 * @returns {Promise<Object>} Созданный комментарий
 */
export const createComment = (duelId, data) => {
    return api.post(`/duels/${duelId}/comments`, data).then((res) => res.data);
};

/**
 * Получить комментарии дуэли с пагинацией
 * @param {string} duelId - ID дуэли
 * @param {Object} params - Параметры запроса
 * @param {number} [params.page=0] - Номер страницы
 * @param {number} [params.size=20] - Размер страницы (макс. 100)
 * @param {boolean} [params.visibleOnly=true] - Только видимые комментарии
 * @returns {Promise<Object>} Пагинированный список комментариев
 */
export const getComments = (duelId, params = {}) => {
    return api.get(`/duels/${duelId}/comments`, { params }).then((res) => res.data);
};

/**
 * Получить мои комментарии в дуэли
 * @param {string} duelId - ID дуэли
 * @param {Object} params - Параметры запроса
 * @param {number} [params.page=0] - Номер страницы
 * @param {number} [params.size=20] - Размер страницы
 * @returns {Promise<Object>} Пагинированный список моих комментариев
 */
export const getMyComments = (duelId, params = {}) => {
    return api.get(`/duels/${duelId}/comments/my`, { params }).then((res) => res.data);
};

/**
 * Получить ответы на комментарий
 * @param {string} duelId - ID дуэли
 * @param {string} commentId - ID комментария
 * @param {Object} params - Параметры запроса
 * @param {number} [params.page=0] - Номер страницы
 * @param {number} [params.size=20] - Размер страницы
 * @returns {Promise<Object>} Пагинированный список ответов
 */
export const getReplies = (duelId, commentId, params = {}) => {
    return api.get(`/duels/${duelId}/comments/${commentId}/replies`, { params }).then((res) => res.data);
};

/**
 * Обновить свой комментарий
 * @param {string} duelId - ID дуэли
 * @param {string} commentId - ID комментария
 * @param {Object} data - Данные для обновления
 * @param {string} data.content - Новый текст комментария
 * @returns {Promise<Object>} Обновлённый комментарий
 */
export const updateComment = (duelId, commentId, data) => {
    return api.put(`/duels/${duelId}/comments/${commentId}`, data).then((res) => res.data);
};

/**
 * Удалить свой комментарий
 * @param {string} duelId - ID дуэли
 * @param {string} commentId - ID комментария
 * @returns {Promise<void>}
 */
export const deleteComment = (duelId, commentId) => {
    return api.delete(`/duels/${duelId}/comments/${commentId}`).then((res) => res.data);
};

/**
 * Лайкнуть комментарий (toggle)
 * @param {string} duelId - ID дуэли
 * @param {string} commentId - ID комментария
 * @returns {Promise<{commentId: string, isLiked: boolean}>} Статус лайка
 */
export const toggleLike = (duelId, commentId) => {
    return api.post(`/duels/${duelId}/comments/${commentId}/like`).then((res) => res.data);
};

/**
 * Модерировать комментарий (ADMIN/MODERATOR)
 * @param {string} duelId - ID дуэли
 * @param {string} commentId - ID комментария
 * @param {Object} data - Данные модерации
 * @param {string} data.action - Действие: HIDE или RESTORE
 * @param {string} [data.reason] - Причина модерации (до 500 символов)
 * @returns {Promise<Object>} Обновлённый комментарий
 */
export const moderateComment = (duelId, commentId, data) => {
    return api.post(`/duels/${duelId}/comments/${commentId}/moderate`, data).then((res) => res.data);
};

/**
 * Получить скрытые комментарии (ADMIN/MODERATOR)
 * @param {string} duelId - ID дуэли
 * @param {Object} params - Параметры запроса
 * @param {number} [params.page=0] - Номер страницы
 * @param {number} [params.size=20] - Размер страницы
 * @returns {Promise<Object>} Пагинированный список скрытых комментариев
 */
export const getHiddenComments = (duelId, params = {}) => {
    return api.get(`/duels/${duelId}/comments/hidden`, { params }).then((res) => res.data);
};

/**
 * Получить статистику комментариев дуэли (ADMIN/MODERATOR)
 * @param {string} duelId - ID дуэли
 * @returns {Promise<Object>} Статистика комментариев
 */
export const getCommentsStats = (duelId) => {
    return api.get(`/duels/${duelId}/comments/stats`).then((res) => res.data);
};

export default {
    createComment,
    getComments,
    getMyComments,
    getReplies,
    updateComment,
    deleteComment,
    toggleLike,
    moderateComment,
    getHiddenComments,
    getCommentsStats
};
